/**
 * Formatting helpers. House style (ui-design §3.13): curly quotes, no em or en
 * dashes in UI copy, middle dots between clauses, no marketing vocabulary.
 *
 * Shared with the server's report export, so a date or a clause list reads the
 * same on the page and in the file.
 */

const MONTHS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
] as const;

/** `YYYY-MM-DD` parsed as a calendar date, never shifted by the viewer's zone. */
function parseDateString(value: string): { year: number; month: number; day: number } | null {
  const match = /^(\d{4})-(\d{2})(?:-(\d{2}))?/.exec(value);
  if (!match) return null;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = match[3] ? Number(match[3]) : 1;
  if (!Number.isFinite(year) || month < 1 || month > 12) return null;
  return { year, month, day };
}

function monthName(month: number): string {
  return MONTHS[month - 1] ?? '';
}

/** "Aug 18, 2026" from an ISO instant. */
export function formatDay(timestamp: string | null | undefined): string {
  if (!timestamp) return '';
  const at = new Date(timestamp);
  if (Number.isNaN(at.getTime())) return '';
  return `${monthName(at.getMonth() + 1)} ${at.getDate()}, ${at.getFullYear()}`;
}

/** "Aug 18, 2026" from a `YYYY-MM-DD` calendar date. */
export function formatDate(value: string | null | undefined): string {
  if (!value) return '';
  const parts = parseDateString(value);
  if (!parts) return value;
  return `${monthName(parts.month)} ${parts.day}, ${parts.year}`;
}

/** "Mar 2024" — month precision, the honest grain for corroboration dating. */
export function formatMonth(value: string | null | undefined): string {
  if (!value) return '';
  const parts = parseDateString(value);
  if (!parts) return value;
  return `${monthName(parts.month)} ${parts.year}`;
}

/**
 * A `LooseDate` at the grain it arrived — "Aug 31, 2023", "Mar 2024", "2019".
 *
 * The projection that fills these (server `date-parse.ts`) states exactly one
 * grain per bound and states it deliberately: a year in is a year out, and a
 * day in is a day the source wrote down. So the render has to be as faithful in
 * the other direction. Reading every dashed value as a month was the bug — a
 * seat dated to the day printed as "May 2015", and the record page, which used
 * to fall back to the raw field and print the day, quietly lost it the moment
 * the window started winning.
 *
 * A value in none of the three shapes is printed verbatim: the record page
 * still falls back to whatever the source wrote in the field, and "Q3 2019"
 * reads better as itself than as an empty string.
 */
export function formatLoose(value: string | null | undefined): string {
  if (!value) return '';
  if (/^\d{4}$/.test(value)) return value;
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return formatDate(value);
  if (/^\d{4}-\d{2}$/.test(value)) return formatMonth(value);
  return value;
}

/**
 * "3 findings" / "1 finding" — no bare numbers next to a noun.
 *
 * The count is grouped by `formatCount`, so the two ways this app writes a
 * number are one way: a helper that printed "1540 notes" while every count
 * beside it read "1,540" would be the odd one out on the only screen where the
 * difference shows. Nothing counted past a few dozen here until an import
 * arrived, which is why the two ever diverged.
 */
export function plural(count: number, one: string, many: string): string {
  return `${formatCount(count)} ${count === 1 ? one : many}`;
}

/**
 * "1,540" — a count with the thousands grouped.
 *
 * Nothing in this app counted past a few dozen until an import arrived with two
 * and a half thousand people on it, and "1540 notes to read" is a number a
 * person has to stop and parse. Grouping is the only thing this does: no
 * abbreviation, no "1.5k", because the office is reading a receipt.
 */
export function formatCount(count: number): string {
  return count.toLocaleString('en-US');
}

/**
 * What sits between two clauses, exported because one line now builds itself
 * partly out of markup — the organisation header, whose website is a link and
 * whose remaining clauses are text — and a second copy of this string is how
 * that line comes to be punctuated differently from every other one.
 */
export const CLAUSE_SEPARATOR = ' · ';

/** Join clauses the way the demo does: a middle dot, never a dash. */
export function joinClauses(parts: (string | null | undefined)[]): string {
  return keepLines(parts).join(CLAUSE_SEPARATOR);
}

/**
 * `joinClauses` without the joining — the same "a clause that did not happen is
 * absent" rule, for the places that stack their facts down the page instead of
 * across it. Written as a conditional list at the call site and reduced to the
 * lines that exist here, so the shape of what a reader sees stays visible in the
 * builder.
 */
export function keepLines(parts: (string | null | undefined)[]): string[] {
  return parts.filter((part): part is string => Boolean(part && part.length > 0));
}
